import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminService } from '../../services/api';
import '../../styles/Admin.css';

const ProfileApproval = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('pending'); // 'pending', 'approved', 'rejected', 'all'
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  // Vérifier si l'utilisateur est admin
  useEffect(() => {
    if (!currentUser || !currentUser.isAdmin) {
      navigate('/');
    } else {
      loadProfiles();
    }
  }, [currentUser, navigate]);
  
  // Charger la liste des profils
  const loadProfiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getAllProfiles();
      setProfiles(data);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des profils');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Filtrer les profils par terme de recherche et statut
  const filteredProfiles = profiles.filter(profile => {
    const matchesSearch = 
      profile.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (profile.user && profile.user.email && profile.user.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (filter === 'all') return matchesSearch;
    if (filter === 'pending') return matchesSearch && profile.status === 'pending';
    if (filter === 'approved') return matchesSearch && profile.status === 'approved';
    if (filter === 'rejected') return matchesSearch && profile.status === 'rejected';
    
    return matchesSearch;
  });
  
  // Approuver un profil
  const handleApproveProfile = async (profileId) => {
    try {
      await adminService.approveProfile(profileId);
      // Mettre à jour la liste des profils
      setProfiles(profiles.map(profile => 
        profile._id === profileId ? { ...profile, status: 'approved' } : profile
      ));
      setShowApprovalModal(false);
      setSelectedProfile(null);
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'approbation du profil');
      console.error('Erreur:', err);
    }
  };
  
  // Rejeter un profil
  const handleRejectProfile = async (profileId, reason) => {
    try {
      await adminService.rejectProfile(profileId, reason);
      // Mettre à jour la liste des profils
      setProfiles(profiles.map(profile => 
        profile._id === profileId ? { ...profile, status: 'rejected', rejectionReason: reason } : profile
      ));
      setShowRejectModal(false);
      setSelectedProfile(null);
      setRejectReason('');
    } catch (err) {
      setError(err.message || 'Erreur lors du rejet du profil');
      console.error('Erreur:', err);
    }
  };
  
  // Ouvrir le modal d'approbation
  const openApprovalModal = (profile) => {
    setSelectedProfile(profile);
    setShowApprovalModal(true);
  };
  
  // Ouvrir le modal de rejet
  const openRejectModal = (profile) => {
    setSelectedProfile(profile);
    setShowRejectModal(true);
  };
  
  // Fermer les modals
  const closeModals = () => {
    setShowApprovalModal(false);
    setShowRejectModal(false);
    setSelectedProfile(null);
    setRejectReason('');
  };
  
  // Afficher le statut du profil
  const getProfileStatus = (profile) => {
    if (profile.status === 'approved') {
      return <span className="status-badge approved">Approuvé</span>;
    } else if (profile.status === 'rejected') {
      return <span className="status-badge rejected">Rejeté</span>;
    } else {
      return <span className="status-badge pending">En attente</span>;
    }
  };
  
  return (
    <div className="admin-profile-approval">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Approbation des profils</h1>
          <button className="btn btn-outline" onClick={() => navigate('/admin')}>
            Retour au tableau de bord
          </button>
        </div>
        
        {error && <div className="alert alert-danger">{error}</div>}
        
        <div className="filters-container">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Rechercher un profil..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="status-filters">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              Tous
            </button>
            <button 
              className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
              onClick={() => setFilter('pending')}
            >
              En attente
            </button>
            <button 
              className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
              onClick={() => setFilter('approved')}
            >
              Approuvés
            </button>
            <button 
              className={`filter-btn ${filter === 'rejected' ? 'active' : ''}`}
              onClick={() => setFilter('rejected')}
            >
              Rejetés
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="loading">Chargement des profils...</div>
        ) : (
          <div className="profile-list">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Expertise</th>
                  <th>Statut</th>
                  <th>Date de modification</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProfiles.length > 0 ? (
                  filteredProfiles.map(profile => (
                    <tr key={profile._id}>
                      <td>{profile.firstName} {profile.lastName}</td>
                      <td>{profile.user && profile.user.email}</td>
                      <td>
                        {profile.expertise && profile.expertise.length > 0 ? (
                          <div className="expertise-tags">
                            {profile.expertise.slice(0, 3).map((exp, index) => (
                              <span key={index} className="expertise-tag">{exp}</span>
                            ))}
                            {profile.expertise.length > 3 && (
                              <span className="expertise-tag more">+{profile.expertise.length - 3}</span>
                            )}
                          </div>
                        ) : (
                          <span className="no-data">Aucune expertise</span>
                        )}
                      </td>
                      <td>{getProfileStatus(profile)}</td>
                      <td>{new Date(profile.updatedAt).toLocaleDateString()}</td>
                      <td className="actions">
                        <button 
                          className="btn btn-sm btn-outline"
                          onClick={() => navigate(`/admin/profiles/${profile._id}`)}
                        >
                          Voir détails
                        </button>
                        {profile.status !== 'approved' && (
                          <button 
                            className="btn btn-sm btn-success"
                            onClick={() => openApprovalModal(profile)}
                          >
                            Approuver
                          </button>
                        )}
                        {profile.status !== 'rejected' && (
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => openRejectModal(profile)}
                          >
                            Rejeter
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="no-results">Aucun profil trouvé</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Modal d'approbation */}
        {showApprovalModal && selectedProfile && (
          <div className="modal-overlay">
            <div className="custom-modal">
              <div className="modal-header">
                <h2>Approuver le profil</h2>
                <button className="close-btn" onClick={closeModals}>&times;</button>
              </div>
              <div className="modal-body">
                <p>Êtes-vous sûr de vouloir approuver le profil de {selectedProfile.firstName} {selectedProfile.lastName}?</p>
                <p>Ce profil sera visible publiquement dans le répertoire.</p>
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-outline"
                  onClick={closeModals}
                >
                  Annuler
                </button>
                <button 
                  className="btn btn-success"
                  onClick={() => handleApproveProfile(selectedProfile._id)}
                >
                  Approuver
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal de rejet */}
        {showRejectModal && selectedProfile && (
          <div className="modal-overlay">
            <div className="custom-modal">
              <div className="modal-header">
                <h2>Rejeter le profil</h2>
                <button className="close-btn" onClick={closeModals}>&times;</button>
              </div>
              <div className="modal-body">
                <p>Veuillez indiquer la raison du rejet du profil de {selectedProfile.firstName} {selectedProfile.lastName}:</p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Raison du rejet..."
                  className="reject-reason-textarea"
                  rows={4}
                />
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-outline"
                  onClick={closeModals}
                >
                  Annuler
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={() => handleRejectProfile(selectedProfile._id, rejectReason)}
                  disabled={!rejectReason.trim()}
                >
                  Rejeter
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileApproval;
